import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TourHighlightService {
  private renderer: Renderer2;
  private highlightedElements: Set<HTMLElement> = new Set();
  private backdrop: HTMLElement | null = null;

  constructor(private rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Highlight an element with specified style and color
   */
  highlightElement(element: HTMLElement, style: 'border' | 'spotlight' | 'pulse', color: 'cyan' | 'purple' = 'cyan'): void {
    // Add element to tracked set
    this.highlightedElements.add(element);

    // Apply base highlight class
    this.renderer.addClass(element, 'tour-highlight');
    
    // Apply color-specific class
    this.renderer.addClass(element, `tour-highlight-${color}`);

    // Apply style-specific classes
    switch (style) {
      case 'border':
        // Base classes already applied
        break;
      case 'spotlight':
        this.renderer.addClass(element, 'tour-spotlight');
        this.createSpotlightBackdrop(element);
        break;
      case 'pulse':
        this.renderer.addClass(element, 'tour-highlight-pulse');
        break;
    }

    // Set z-index to ensure it's above other content
    const originalZIndex = element.style.zIndex;
    this.renderer.setStyle(element, 'z-index', '100000');
    element.setAttribute('data-original-z-index', originalZIndex || '');
  }

  /**
   * Show spotlight backdrop for an element by CSS selector
   * This obscures everything except the specified element with a semi-transparent backdrop
   */
  showSpotlightForSelector(selector: string): void {
    // Wait a bit for DOM to be ready, then find and spotlight the element
    setTimeout(() => {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        // Ensure element is above backdrop
        const originalZIndex = element.style.zIndex;
        const originalPosition = element.style.position;
        // Use computed style to check actual position (not just inline style)
        const computedPosition = window.getComputedStyle(element).position;
        this.renderer.setStyle(element, 'z-index', '100001');
        // Only set position: relative if the element is truly static (not absolute, fixed, etc.)
        if (computedPosition === 'static') {
          this.renderer.setStyle(element, 'position', 'relative');
        }
        element.setAttribute('data-spotlight-original-z-index', originalZIndex || '');
        element.setAttribute('data-spotlight-original-position', originalPosition || '');
        element.setAttribute('data-spotlight-active', 'true');
        
        this.createSpotlightBackdrop(element);
      }
    }, 300);
  }

  /**
   * Remove spotlight backdrop
   */
  removeSpotlight(): void {
    // Find any elements with spotlight active and restore their styles
    const spotlightElements = document.querySelectorAll('[data-spotlight-active="true"]');
    spotlightElements.forEach(el => {
      const element = el as HTMLElement;
      const originalZIndex = element.getAttribute('data-spotlight-original-z-index');
      const originalPosition = element.getAttribute('data-spotlight-original-position');
      
      if (originalZIndex) {
        this.renderer.setStyle(element, 'z-index', originalZIndex);
      } else {
        this.renderer.removeStyle(element, 'z-index');
      }
      
      if (originalPosition) {
        this.renderer.setStyle(element, 'position', originalPosition);
      } else {
        this.renderer.removeStyle(element, 'position');
      }
      
      element.removeAttribute('data-spotlight-original-z-index');
      element.removeAttribute('data-spotlight-original-position');
      element.removeAttribute('data-spotlight-active');
    });
    
    this.removeBackdrop();
  }

  /**
   * Remove highlight from specific element
   */
  removeHighlight(element: HTMLElement): void {
    this.renderer.removeClass(element, 'tour-highlight');
    this.renderer.removeClass(element, 'tour-highlight-cyan');
    this.renderer.removeClass(element, 'tour-highlight-purple');
    this.renderer.removeClass(element, 'tour-spotlight');
    this.renderer.removeClass(element, 'tour-highlight-pulse');

    // Restore original z-index
    const originalZIndex = element.getAttribute('data-original-z-index');
    if (originalZIndex) {
      this.renderer.setStyle(element, 'z-index', originalZIndex);
    } else {
      this.renderer.removeStyle(element, 'z-index');
    }
    element.removeAttribute('data-original-z-index');

    this.highlightedElements.delete(element);

    // Remove backdrop if no more spotlight elements
    if (!this.hasSpotlightElements()) {
      this.removeBackdrop();
    }
  }

  /**
   * Clear all highlights
   */
  clearAllHighlights(): void {
    this.highlightedElements.forEach(element => {
      this.removeHighlight(element);
    });
    this.highlightedElements.clear();
    this.removeBackdrop();
  }

  /**
   * Create backdrop for spotlight effect
   */
  private createSpotlightBackdrop(element: HTMLElement): void {
    // Remove existing backdrop
    this.removeBackdrop();

    // Create new backdrop
    this.backdrop = this.renderer.createElement('div');
    this.renderer.addClass(this.backdrop, 'tour-spotlight-backdrop');
    
    // Calculate clip-path to cut out the highlighted element
    const rect = element.getBoundingClientRect();
    const padding = 8; // Padding around the element
    
    const clipPath = `polygon(
      0 0,
      100% 0,
      100% 100%,
      0 100%,
      0 0,
      ${rect.left - padding}px ${rect.top - padding}px,
      ${rect.left - padding}px ${rect.bottom + padding}px,
      ${rect.right + padding}px ${rect.bottom + padding}px,
      ${rect.right + padding}px ${rect.top - padding}px,
      ${rect.left - padding}px ${rect.top - padding}px
    )`;
    
    this.renderer.setStyle(this.backdrop, 'clip-path', clipPath);
    this.renderer.appendChild(document.body, this.backdrop);

    // Update backdrop on window resize
    const resizeHandler = () => {
      if (this.backdrop && element) {
        const newRect = element.getBoundingClientRect();
        const newClipPath = `polygon(
          0 0,
          100% 0,
          100% 100%,
          0 100%,
          0 0,
          ${newRect.left - padding}px ${newRect.top - padding}px,
          ${newRect.left - padding}px ${newRect.bottom + padding}px,
          ${newRect.right + padding}px ${newRect.bottom + padding}px,
          ${newRect.right + padding}px ${newRect.top - padding}px,
          ${newRect.left - padding}px ${newRect.top - padding}px
        )`;
        this.renderer.setStyle(this.backdrop, 'clip-path', newClipPath);
      }
    };

    window.addEventListener('resize', resizeHandler);
    window.addEventListener('scroll', resizeHandler);

    // Store cleanup function
    if (this.backdrop) {
      (this.backdrop as any).__cleanupHandler = () => {
        window.removeEventListener('resize', resizeHandler);
        window.removeEventListener('scroll', resizeHandler);
      };
    }
  }

  /**
   * Remove backdrop
   */
  private removeBackdrop(): void {
    if (this.backdrop) {
      // Call cleanup handler if it exists
      if ((this.backdrop as any).__cleanupHandler) {
        (this.backdrop as any).__cleanupHandler();
      }
      this.renderer.removeChild(document.body, this.backdrop);
      this.backdrop = null;
    }
  }

  /**
   * Check if any elements have spotlight class
   */
  private hasSpotlightElements(): boolean {
    return Array.from(this.highlightedElements).some(el => 
      el.classList.contains('tour-spotlight')
    );
  }
}
